import { useEffect, useRef, useState } from 'react';
import { ImageOff, Smartphone } from 'lucide-react';
import clsx from 'clsx';
import { API_CREDENTIALS, withApiBase } from '../lib/apiBase';

/**
 * Live thumbnail backed by `GET /api/devices/:serial/thumbnail`. The
 * endpoint returns a fresh PNG every 5 s (server-side LRU); on the
 * client we refetch on the same cadence and stream the result through
 * an object URL so React can swap the `<img src>` without flashing.
 *
 * Error handling:
 *   - 403 (device went private / forgot) → render the "no preview"
 *     placeholder; the parent grid will re-list and remove the card.
 *   - 5xx (device offline / authorisation issues / adb hiccup) → keep
 *     showing the last good frame if any, else placeholder.
 *
 * The component is intentionally `<img>`-based (no canvas) so the
 * browser cache + decode pipeline does the heavy lifting; we just blob-
 * url so the request honours `credentials: 'include'`.
 */
export function DeviceThumbnail({
  serial,
  /** Whether the device is online enough to bother fetching. Offline → render placeholder. */
  online = true,
  /** Per-instance refresh cadence (ms). Default 5 s. */
  intervalMs = 5_000,
  className,
}: {
  serial: string;
  online?: boolean;
  intervalMs?: number;
  className?: string;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  // Revoke previous blob URLs to keep memory tidy as the cadence ticks.
  const lastUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (!online) {
      setSrc(null);
      setFailed(false);
      return;
    }
    let cancelled = false;
    let timer: number | undefined;

    const refresh = async (): Promise<void> => {
      try {
        const res = await fetch(
          withApiBase(`/api/devices/${encodeURIComponent(serial)}/thumbnail`),
          { credentials: API_CREDENTIALS }
        );
        if (cancelled) return;
        if (!res.ok) {
          setFailed(true);
          return;
        }
        const blob = await res.blob();
        if (cancelled) return;
        const next = URL.createObjectURL(blob);
        const prev = lastUrlRef.current;
        lastUrlRef.current = next;
        setSrc(next);
        setFailed(false);
        if (prev) URL.revokeObjectURL(prev);
      } catch {
        if (cancelled) return;
        setFailed(true);
      }
    };

    void refresh();
    timer = window.setInterval(refresh, intervalMs);
    return (): void => {
      cancelled = true;
      if (timer !== undefined) window.clearInterval(timer);
      if (lastUrlRef.current) {
        URL.revokeObjectURL(lastUrlRef.current);
        lastUrlRef.current = null;
      }
    };
  }, [serial, online, intervalMs]);

  if (!online) {
    return (
      <Placeholder className={className} hint="离线 · offline">
        <Smartphone className="h-5 w-5 text-ink-dim" />
      </Placeholder>
    );
  }
  if (failed && !src) {
    return (
      <Placeholder className={className} hint="无预览 · no preview">
        <ImageOff className="h-5 w-5 text-ink-dim" />
      </Placeholder>
    );
  }
  if (!src) {
    return <Placeholder className={className} hint="加载中…" />;
  }
  return (
    <div className={clsx('overflow-hidden rounded-md border border-line bg-black/5', className)}>
      <img
        src={src}
        alt={`thumbnail of ${serial}`}
        className="h-full w-full object-contain"
        draggable={false}
      />
    </div>
  );
}

function Placeholder({
  className,
  hint,
  children,
}: {
  className?: string;
  hint: string;
  children?: React.ReactNode;
}) {
  return (
    <div
      className={clsx(
        'flex items-center justify-center rounded-md border border-dashed border-line bg-bg-soft',
        className
      )}
    >
      <div className="flex flex-col items-center gap-1 text-[10px] text-ink-dim">
        {children}
        <span>{hint}</span>
      </div>
    </div>
  );
}
